import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Trophy,
  Zap,
  BarChart3,
  Sparkles,
  UserPlus,
  LogIn,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Activity,
  Share2,
  Users,
  FileText
} from "lucide-react";

export default function Home() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] selection:bg-[#FF3B30] selection:text-white font-sans">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-lg bg-[#FF3B30] flex items-center justify-center text-white font-black text-xl shadow-[0_0_15px_rgba(255,59,48,0.4)] group-hover:scale-105 transition-transform">
              CP
            </div>
            <div>
              <h1 className="text-xl font-black uppercase tracking-wider text-white flex items-center gap-2">
                CRICKETPULSE
              </h1>
              <span className="text-[10px] font-mono text-[#A3A3A3] uppercase block -mt-1">
                AI Performance Intelligence
              </span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-[#A3A3A3]">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#ai-coach" className="hover:text-white transition-colors">AI Coach</a>
            <a href="#analytics" className="hover:text-white transition-colors">Analytics Engine</a>
            <a href="#profiles" className="hover:text-white transition-colors">Public Profiles</a>
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <>
                <button
                  data-testid="home-dashboard-btn"
                  onClick={() => navigate("/dashboard")}
                  className="bg-[#FF3B30] hover:bg-[#E03229] text-white text-xs font-bold px-4 py-2.5 rounded flex items-center gap-2 transition-all shadow-lg"
                >
                  <Activity className="w-4 h-4" /> Go to Dashboard
                </button>
                <button
                  onClick={logout}
                  className="bg-[#1A1A1A] hover:bg-[#262626] text-[#A3A3A3] hover:text-white text-xs font-semibold px-3 py-2 rounded border border-[#262626]"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  data-testid="home-login-btn"
                  to="/login"
                  className="bg-[#1A1A1A] hover:bg-[#262626] text-white text-xs font-bold px-4 py-2.5 rounded border border-[#262626] flex items-center gap-1.5 transition-all"
                >
                  <LogIn className="w-3.5 h-3.5 text-[#FF3B30]" /> Sign In
                </Link>
                <Link
                  data-testid="home-register-btn"
                  to="/register"
                  className="bg-[#FF3B30] hover:bg-[#E03229] text-white text-xs font-bold px-4 py-2.5 rounded flex items-center gap-1.5 transition-all shadow-lg"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Register Free
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-16 pb-24 px-6 overflow-hidden border-b border-[#262626]">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FF3B30]/10 rounded-full blur-[140px] pointer-events-none" />
        
        <div className="max-w-5xl mx-auto text-center space-y-8 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1A1A1A] border border-yellow-400/30 text-yellow-400 text-xs font-mono font-bold uppercase shadow-xl animate-fade-in">
            <Zap className="w-4 h-4 text-yellow-400 fill-yellow-400" /> Powered by Gemini 2.5 Flash & Pandas Engine
          </div>

          <h1 className="text-4xl sm:text-6xl md:text-7xl font-black uppercase text-white tracking-tight leading-[0.95]">
            Elevate Your Cricket Game With <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF3B30] via-red-400 to-yellow-400">AI Intelligence</span>
          </h1>

          <p className="max-w-3xl mx-auto text-base sm:text-lg text-[#A3A3A3] leading-relaxed font-normal">
            The next-generation performance analytics platform for modern cricketers. Log your match scorecards, analyze phase breakdown metrics, and receive elite AI coaching dossiers with <strong className="text-white">3 customized drill prescriptions</strong>.
          </p>

          <div className="flex flex-wrap justify-center items-center gap-4 pt-4">
            {user ? (
              <Link
                to="/dashboard"
                className="bg-[#FF3B30] hover:bg-[#E03229] text-white font-bold text-sm px-8 py-4 rounded-xl shadow-[0_0_25px_rgba(255,59,48,0.4)] flex items-center gap-3 transition-all hover:scale-105"
              >
                Open Your Dashboard <ArrowRight className="w-5 h-5" />
              </Link>
            ) : (
              <>
                <Link
                  data-testid="hero-register-btn"
                  to="/register"
                  className="bg-[#FF3B30] hover:bg-[#E03229] text-white font-bold text-sm px-8 py-4 rounded-xl shadow-[0_0_25px_rgba(255,59,48,0.4)] flex items-center gap-3 transition-all hover:scale-105"
                >
                  Start Free Registration <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  data-testid="hero-login-btn"
                  to="/login"
                  className="bg-[#141414] hover:bg-[#1A1A1A] text-white font-bold text-sm px-8 py-4 rounded-xl border border-[#262626] flex items-center gap-2 transition-all hover:border-[#FF3B30]/50"
                >
                  Player Sign In <LogIn className="w-4 h-4 text-[#A3A3A3]" />
                </Link>
              </>
            )}
          </div>

          {/* Key Metrics Strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto pt-12 text-left">
            <div className="bg-[#141414] border border-[#262626] rounded-xl p-4">
              <span className="text-2xl font-black text-white block">100%</span>
              <span className="text-xs text-[#A3A3A3]">Stats-Grounded Analysis</span>
            </div>
            <div className="bg-[#141414] border border-[#262626] rounded-xl p-4">
              <span className="text-2xl font-black text-yellow-400 block">Gemini 2.5</span>
              <span className="text-xs text-[#A3A3A3]">Flash AI Coach Engine</span>
            </div>
            <div className="bg-[#141414] border border-[#262626] rounded-xl p-4">
              <span className="text-2xl font-black text-white block">3 Drills</span>
              <span className="text-xs text-[#A3A3A3]">Targeted Prescriptions</span>
            </div>
            <div className="bg-[#141414] border border-[#262626] rounded-xl p-4">
              <span className="text-2xl font-black text-emerald-400 block">HD PDF</span>
              <span className="text-xs text-[#A3A3A3]">High-Contrast Export</span>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Section */}
      <section id="features" className="py-20 px-6 max-w-7xl mx-auto space-y-16">
        <div className="text-center space-y-3 max-w-2xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-[#FF3B30] bg-[#FF3B30]/10 px-3 py-1 rounded border border-[#FF3B30]/30">
            Core Features
          </span>
          <h2 className="text-3xl sm:text-4xl font-black uppercase text-white">
            Built for Serious Cricketers
          </h2>
          <p className="text-sm text-[#A3A3A3]">
            From local club matches to competitive leagues, track every delivery and transform raw statistics into actionable insights.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Card 1 */}
          <div className="bg-[#141414] border border-[#262626] rounded-2xl p-8 space-y-4 hover:border-[#FF3B30]/40 transition-colors group">
            <div className="w-12 h-12 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] flex items-center justify-center font-bold group-hover:scale-110 transition-transform">
              <Trophy className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold uppercase text-white">Match Scorecard Logging</h3>
            <p className="text-sm text-[#A3A3A3] leading-relaxed">
              Log batting (runs, balls, 4s, 6s, dismissal type, bowler style) and bowling (overs, wickets, runs, dot balls) with detailed phase breakdowns (Powerplay, Middle, Death).
            </p>
          </div>

          {/* Card 2 */}
          <div id="analytics" className="bg-[#141414] border border-[#262626] rounded-2xl p-8 space-y-4 hover:border-[#FF3B30]/40 transition-colors group">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-400 flex items-center justify-center font-bold group-hover:scale-110 transition-transform">
              <BarChart3 className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold uppercase text-white">Pandas Analytics Engine</h3>
            <p className="text-sm text-[#A3A3A3] leading-relaxed">
              Automated computation of averages, strike rates, economy rates, boundary %, dot-ball %, dismissal patterns, and an interactive 6-axis Skill Radar profile.
            </p>
          </div>

          {/* Card 3 */}
          <div id="ai-coach" className="bg-[#141414] border border-[#262626] rounded-2xl p-8 space-y-4 hover:border-yellow-400/40 transition-colors group">
            <div className="w-12 h-12 rounded-xl bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 flex items-center justify-center font-bold group-hover:scale-110 transition-transform">
              <Sparkles className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold uppercase text-white">Gemini 2.5 Flash AI Coach</h3>
            <p className="text-sm text-[#A3A3A3] leading-relaxed">
              Generate a national-level coaching dossier. Identifies 3 technical strengths, 3 tactical weaknesses, priority focus area, and prescribes exactly 3 customized drills.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive AI Coach Demo Banner */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0A0A0A] via-[#141414] to-[#0A0A0A] border-y border-[#262626]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-xs font-mono font-bold uppercase">
              <ShieldCheck className="w-4 h-4" /> Evidence-Based Analysis
            </div>
            <h2 className="text-3xl sm:text-5xl font-black uppercase text-white leading-tight">
              No Generic Advice. <br /><span className="text-[#FF3B30]">Strictly Your Stats.</span>
            </h2>
            <p className="text-base text-[#A3A3A3] leading-relaxed">
              Our AI coach model doesn't output fluff. High dot-ball percentage influences shot-selection drills; poor economy rate influences death-over yorker drills; high boundary rate is recognized as a key strength.
            </p>

            <ul className="space-y-3 text-sm text-[#F5F5F5]">
              <li className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <span>Overall player performance assessment & form rating (0–100)</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <span>Exactly 3 tailored training prescriptions with objectives & frequency</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <span>Exportable clean PDF dossier for offline training</span>
              </li>
            </ul>

            <div className="pt-4 flex flex-wrap gap-4">
              <Link
                to={user ? "/ai-coach" : "/register"}
                className="bg-[#FF3B30] hover:bg-[#E03229] text-white font-bold text-xs px-6 py-3 rounded flex items-center gap-2 transition-all shadow-lg"
              >
                <Sparkles className="w-4 h-4" /> {user ? "Generate Coach Report" : "Get Your Coach Report"}
              </Link>
            </div>
          </div>

          {/* Mock Card Preview */}
          <div className="bg-[#141414] border border-[#262626] rounded-2xl p-6 shadow-2xl space-y-4 relative overflow-hidden">
            <div className="flex items-center justify-between border-b border-[#262626] pb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                <span className="text-xs font-mono font-bold uppercase text-white">Gemini 2.5 Flash Coach Dossier</span>
              </div>
              <span className="bg-[#FF3B30] text-white text-[10px] font-mono font-bold px-2 py-0.5 rounded">Form: 88/100</span>
            </div>

            <div className="space-y-2">
              <span className="text-[10px] font-mono text-yellow-400 uppercase tracking-widest">Overall Assessment</span>
              <p className="text-xs text-[#A3A3A3] leading-relaxed">
                Dominant top-order stroke play with a 142.5 strike rate. Displays exceptional boundary frequency against pace, but requires tighter defensive footwork against leg-spin in the middle overs.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="bg-[#1A1A1A] border border-emerald-500/30 rounded p-3 space-y-1">
                <span className="text-[10px] font-bold text-emerald-400 uppercase">Key Strength</span>
                <p className="text-[11px] text-white font-semibold">Boundary Hitting Efficiency (68% boundary runs)</p>
              </div>
              <div className="bg-[#1A1A1A] border border-[#FF3B30]/30 rounded p-3 space-y-1">
                <span className="text-[10px] font-bold text-[#FF3B30] uppercase">Priority Area</span>
                <p className="text-[11px] text-white font-semibold">Leg-Spin Middle Overs Rotation</p>
              </div>
            </div>

            <div className="bg-[#1A1A1A] border border-[#262626] rounded p-3 space-y-1">
              <div className="flex justify-between items-center text-[10px] font-mono font-bold text-yellow-400">
                <span>DRILL 01 • BATTING</span>
                <span className="text-emerald-400">HIGH PRIORITY</span>
              </div>
              <p className="text-xs font-bold text-white">Wrist-Spin Drop-Ball & Sweep Circuit</p>
              <p className="text-[11px] text-[#A3A3A3]">Target: Reduce dot-ball percentage against spin from 42% to under 25%.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Public Profiles & Sharing (`#profiles`) */}
      <section id="profiles" className="py-20 px-6 max-w-7xl mx-auto space-y-12">
        <div className="text-center space-y-3 max-w-2xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded border border-emerald-500/30">
            Public Profile & Sharing
          </span>
          <h2 className="text-3xl sm:text-4xl font-black uppercase text-white">
            Share Your Cricket Card Anywhere
          </h2>
          <p className="text-sm text-[#A3A3A3]">
            Generate a verified public player snapshot, share your performance card with recruiters, or compare stats head-to-head with teammates.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-3">
            <Share2 className="w-8 h-8 text-[#FF3B30]" />
            <h4 className="text-lg font-bold uppercase text-white">Share Token URLs</h4>
            <p className="text-xs text-[#A3A3A3]">Enable secure, read-only public links to showcase your stats without giving access to your account.</p>
          </div>

          <div className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-3">
            <Users className="w-8 h-8 text-blue-400" />
            <h4 className="text-lg font-bold uppercase text-white">Head-to-Head Compare</h4>
            <p className="text-xs text-[#A3A3A3]">Compare two players side-by-side on batting averages, strike rates, economy rates, and Skill Radars.</p>
          </div>

          <div className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-3">
            <FileText className="w-8 h-8 text-yellow-400" />
            <h4 className="text-lg font-bold uppercase text-white">Print & PDF Export</h4>
            <p className="text-xs text-[#A3A3A3]">Download high-contrast black & white PDF dossiers formatted cleanly for print or digital sharing.</p>
          </div>
        </div>
      </section>

      {/* CTA Footer Banner */}
      <section className="py-16 px-6 bg-[#141414] border-t border-[#262626]">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl sm:text-5xl font-black uppercase text-white">
            Ready to Upgrade Your Cricket Performance?
          </h2>
          <p className="text-sm sm:text-base text-[#A3A3A3] max-w-xl mx-auto">
            Join CricketPulse today, log your matches, and let Gemini 2.5 Flash guide your training roadmap.
          </p>

          <div className="flex flex-wrap justify-center gap-4 pt-2">
            {user ? (
              <Link
                to="/dashboard"
                className="bg-[#FF3B30] hover:bg-[#E03229] text-white font-bold text-sm px-8 py-3.5 rounded-xl shadow-lg transition-all"
              >
                Go to Dashboard
              </Link>
            ) : (
              <>
                <Link
                  data-testid="cta-register-btn"
                  to="/register"
                  className="bg-[#FF3B30] hover:bg-[#E03229] text-white font-bold text-sm px-8 py-3.5 rounded-xl shadow-lg transition-all"
                >
                  Create Free Account
                </Link>
                <Link
                  data-testid="cta-login-btn"
                  to="/login"
                  className="bg-[#1A1A1A] hover:bg-[#262626] text-white font-bold text-sm px-8 py-3.5 rounded-xl border border-[#262626] transition-all"
                >
                  Player Sign In
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#0A0A0A] border-t border-[#262626] text-center text-xs text-[#A3A3A3]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded bg-[#FF3B30] text-white font-black text-xs flex items-center justify-center">CP</span>
            <span className="font-bold uppercase text-white">CricketPulse</span>
            <span>• © 2026 CricketPulse Intelligence</span>
          </div>

          <div className="flex items-center gap-6 font-semibold">
            <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
            <Link to="/register" className="hover:text-white transition-colors">Register</Link>
            {user && <Link to="/dashboard" className="hover:text-white transition-colors">Dashboard</Link>}
          </div>
        </div>
      </footer>
    </div>
  );
}
